import { server } from '@/helpers';
import { z } from 'zod';

const CheckoutStatusEnumSchema = z.enum(['success', 'failed', 'onHold']);

const CheckoutStatusSchema = z.object({
  id: z.number().optional(),
  status: CheckoutStatusEnumSchema.optional(),
  type: z.enum(['booking', 'bundle']).optional(),
  productData: z
    .object({
      id: z.number().optional(),
      bookingBundleId: z.number().optional(),
      startTs: z.string().optional(),
      endTs: z.string().optional(),
    })
    .optional(),
  // Error fields populated when status === 'failed'
  errorCode: z.string().optional(),
  errorMessage: z.string().optional(),
  /** Redirect URL for client to navigate to after error */
  redirectUrl: z.string().optional(),
});

export type CheckoutStatusEnum = z.infer<typeof CheckoutStatusEnumSchema>;
export type CheckoutStatus = z.infer<typeof CheckoutStatusSchema>;

export enum PaymentResultStatus {
  COMPLETED = 'paymentCompleted',
  FAILED = 'paymentFailed',
  PENDING = 'paymentPending',
}

export type PaymentResult =
  | { status: PaymentResultStatus.COMPLETED; resultCode: string }
  | { status: PaymentResultStatus.FAILED; resultCode: string; errorCode?: string; errorMessage?: string; redirectUrl?: string }
  | { status: PaymentResultStatus.PENDING; resultCode: string | null };

/**
 * Get checkout status for a resource
 * Checks the payment status for a checkoutRef. Resource type ('booking' | 'bundle')
 * affects only the URL path; the underlying lookup is keyed by checkoutRef.
 */
function getCheckoutStatus(params: {
  checkoutRef: string;
  resourceType?: 'booking' | 'bundle';
  sessionId?: string;
  sessionResult?: string;
  /** The shared instance sets none; a poll that must settle on its own passes one. */
  timeout?: number;
  /**
   * Skip `handleSuccess`, whose deploy-timestamp check reloads the window: the
   * widget's reconcile poll would lose a `success` to the reload before routing on it.
   */
  skipDeployReload?: boolean;
}): Promise<CheckoutStatus> {
  const { checkoutRef, resourceType = 'booking', sessionId, sessionResult, timeout, skipDeployReload } = params;

  return server.request
    .get(`/checkout/${resourceType}/${checkoutRef}`, {
      params: {
        sessionId,
        sessionResult,
      },
      timeout,
    })
    .then(skipDeployReload ? (response: { data: CheckoutStatus }) => response.data : server.handleSuccess)
    .catch(server.handleErrorObject);
}

/**
 * Submit checkout details (Adyen redirectResult)
 * Returns the payment result status — poll getCheckoutStatus for the full checkout outcome
 */
function submitCheckoutDetails(params: {
  checkoutRef: string;
  resourceType?: 'booking' | 'bundle';
  redirectResult: string;
}): Promise<PaymentResult> {
  const { checkoutRef, resourceType = 'booking', redirectResult } = params;

  return server.request
    .post(`/checkout/${resourceType}/${checkoutRef}/details`, { redirectResult })
    .then(server.handleSuccess)
    .catch(server.handleErrorObject);
}

const CheckoutSessionSchema = z.object({
  sessionId: z.string(),
  sessionData: z.string(),
});

export type CheckoutSession = z.infer<typeof CheckoutSessionSchema>;

// The pay popup shows nothing but a spinner until this resolves and the shared
// axios instance has no timeout, so bound it here to reach the popup's error path.
const CHECKOUT_SESSION_TIMEOUT_MS = 15000;

/**
 * Fetch the pending Adyen session for a checkoutRef. Used by the widget's pay popup,
 * which receives only references on its URL and mounts the session from here.
 */
function getCheckoutSession(params: {
  checkoutRef: string;
  resourceType?: 'booking' | 'bundle';
}): Promise<CheckoutSession> {
  const { checkoutRef, resourceType = 'booking' } = params;

  // Not handleSuccess: its deploy-timestamp check reloads the window, which would
  // restart the popup boot and fire a second session fetch just as the drop-in is
  // about to mount.
  return server.request
    .get(`/checkout/${resourceType}/${checkoutRef}/session`, { timeout: CHECKOUT_SESSION_TIMEOUT_MS })
    .then((response) => response.data)
    .catch(server.handleErrorObject)
    .then((data) => CheckoutSessionSchema.parse(data));
}

export const checkoutServices = {
  getCheckoutStatus,
  getCheckoutSession,
  submitCheckoutDetails,
};
